import dayjs from 'dayjs';

import { PAYMENT_CUSTOM_DATA_ACTION } from '@/types/billing';
import { OfferingType, PLAN, Plan } from '@/types/plan';
import { Subscription } from '@/types/subscription';

// BE consts/enum.ts PLAN_PRIORITY와 동일 (origin_plan 기준 티어 우선순위)
export const ORIGIN_PRIORITY: Record<string, number> = {
  [PLAN.FREE]: 0,
  [PLAN.EFFICIENT]: 1,
  [PLAN.STANDARD]: 2,
  [PLAN.SPECIAL]: 3,
  [PLAN.SPECIAL_PLUS]: 4,
};

/**
 * PROMOTIONAL(첫달무료·두달무료 등) 활성 구독자가 결제할 때 checkout-intent에 실을 action_type을 결정한다.
 *
 * BE checkout-intent는 활성 구독(status=active && end_date>now)이 있으면 SUBSCRIBE를 409로 막으므로
 * 활성 프로모션 구독자는 tier 방향에 따라 아래로 분기한다.
 *
 * - 같은 tier / 다운그레이드: RESERVE
 *     → 무료 기간(end_date) 유지, 종료일에 새 플랜 결제 예약. 즉시 결제 X.
 *       BE handlePaymentOnlyReserve(payments.service.ts)가 RESERVE를 받아 isUpgrade 재판정 후
 *       다운그레이드/유지는 종료일 예약으로 처리한다(무료 소멸 X). 다운그레이드에 무료를 포기시킬 이유가 없음.
 * - 상위 tier 업그레이드: CHANGE_PLAN
 *     → 즉시 전액 결제(무료 포기), 오늘부터 새 주기(today + plan.period).
 *       (프로모션은 낸 돈이 0이라 proration 크레딧이 없어 오늘부터 새 주기로 둔다.)
 * - 만료됐거나 프로모션이 아니면: SUBSCRIBE (신규/재구독).
 *
 * cf. docs/PROMOTIONAL_RESERVE_EXPIRY_MISMATCH.md, docs/FREE_TRIAL_SPECIAL_MIGRATION.md
 */
export type PromotionalPaymentDecision =
  | { actionType: PAYMENT_CUSTOM_DATA_ACTION.RESERVE; paymentDate: Date }
  | { actionType: PAYMENT_CUSTOM_DATA_ACTION.CHANGE_PLAN; nextPaymentDate: Date }
  | { actionType: PAYMENT_CUSTOM_DATA_ACTION.SUBSCRIBE };

export const getPromotionalPaymentDecision = (
  subscription: Subscription | undefined,
  selectedPlan: Plan,
): PromotionalPaymentDecision => {
  const isPromotional = subscription?.plan?.offering_type === OfferingType.PROMOTIONAL;
  const endDate = subscription?.end_date;
  // BE checkout-intent의 `end_date > now` 와 동일한 strict 기준.
  // /user/me 는 종료 후 4h grace로 구독을 계속 내려주지만, 분기는 strict를 따라야 400/409를 피한다.
  const isActive = !!endDate && dayjs(endDate).isAfter(dayjs());

  if (isPromotional && isActive) {
    const currentPriority = ORIGIN_PRIORITY[subscription?.plan?.origin_plan ?? ''] ?? 0;
    const targetPriority = ORIGIN_PRIORITY[selectedPlan.origin_plan] ?? 0;
    const isUpgrade = targetPriority > currentPriority;

    // 같은 tier 또는 다운그레이드 → 무료 유지, 종료일 예약 (BE가 종료일 예약으로 처리)
    if (!isUpgrade) {
      return {
        actionType: PAYMENT_CUSTOM_DATA_ACTION.RESERVE,
        paymentDate: dayjs(endDate).toDate(),
      };
    }
    // 상위 tier 업그레이드 → 즉시 전액 결제, 오늘부터 새 주기
    return {
      actionType: PAYMENT_CUSTOM_DATA_ACTION.CHANGE_PLAN,
      nextPaymentDate: dayjs()
        .add(selectedPlan.period > 0 ? selectedPlan.period : 1, 'month')
        .toDate(),
    };
  }

  return { actionType: PAYMENT_CUSTOM_DATA_ACTION.SUBSCRIBE };
};
